import {
  LandingPageTemplate,
  LandingPageWithVariants,
  LoginMethod,
} from "../types/landingPage";
import { lpTestScreeningQuestions } from "./pre-screener-config/LpTestScreener";

/**
 * DEVELOPMENT ONLY CONFIGURATIONS
 *
 * These configurations are only for development and testing purposes.
 * They will never be included in production builds.
 */
export const developmentLandingPageConfigs: Record<
  string,
  LandingPageWithVariants
> = {
  // Development-friendly T2D landing page - now uses lpTestScreeningQuestions
  "dev-t2d-landing": {
    id: "dev-t2d-landing",
    template: LandingPageTemplate.EXTENDED,
    title: "DEV ONLY - T2D Landing Page | HealthMatch",
    description:
      "Development-friendly T2D landing page with compatible attribute IDs.",
    heroTitle: "DEVELOPMENT VERSION",
    heroSubtitle: "Type 2 Diabetes Management Study",
    introText: `Development Environment: ${import.meta.env.MODE}`,
    longDescription:
      "This landing page uses a simplified set of screening questions that are compatible with the development dataset.\n\n" +
      "Questions mapping to nonexistent attribute IDs have been removed or modified.",
    preScreeningQuestions: lpTestScreeningQuestions,
    conditionId: 304,
    status: "development",
    variants: {
      "dev-t2d-simple": {
        id: "dev-t2d-simple",
        masterId: "dev-t2d-landing",
        heroSubtitle: "Simplified T2D Screener (Dev-Compatible)",
        status: "development",
      },
    },
    countryCode: "CA",
  },
  "dev-t2d-landing-usa": {
    id: "dev-t2d-landing-usa",
    template: LandingPageTemplate.EXTENDED,
    title: "DEV ONLY - T2D Landing Page | HealthMatch",
    description:
      "Development-friendly T2D landing page with compatible attribute IDs.",
    heroTitle: "DEVELOPMENT VERSION",
    heroSubtitle: "Type 2 Diabetes Management Study",
    introText: `Development Environment: ${import.meta.env.MODE}`,
    longDescription:
      "This landing page uses a simplified set of screening questions that are compatible with the development dataset.\n\n" +
      "Questions mapping to nonexistent attribute IDs have been removed or modified.",
    preScreeningQuestions: lpTestScreeningQuestions,
    conditionId: 304,
    status: "development",
    trialIdToAutoApply: {
      defaultTrialId: "NCT123", // Has default trial ID as fallback for users outside age cohorts
      ageCohorts: [
        {
          minAgeInclusive: 30,
          maxAgeExclusive: 66, // Note: maxAgeExclusive needs to be 66 to include ages up to 65
          trialId: "NCT123-ADULT",
        },
      ],
    },
    variants: {
      "dev-t2d-simple": {
        id: "dev-t2d-simple",
        masterId: "dev-t2d-landing",
        heroSubtitle: "Simplified T2D Screener (Dev-Compatible)",
        status: "development",
      },
      "dev-t2d-age-strict": {
        id: "dev-t2d-age-strict",
        masterId: "dev-t2d-landing-usa",
        heroSubtitle: "Age-Restricted T2D Study (No Default Trial ID)",
        status: "development",
        // This configuration is strict about age cohorts - no fallback
        trialIdToAutoApply: {
          // No defaultTrialId - users outside cohorts will be ineligible
          ageCohorts: [
            {
              minAgeInclusive: 40,
              maxAgeExclusive: 61, // Ages 40-60
              trialId: "NCT123-STRICT",
            },
          ],
        },
      },
    },
    countryCode: "US",
    preferredLoginMethod: LoginMethod.SMS,
    redirectConditionId: 305,
  },
  // Canadian landing page example
  "ca-test": {
    id: "ca-test",
    template: LandingPageTemplate.EXTENDED,
    title: "Canadian Test Page | HealthMatch",
    description:
      "This is a test landing page for Canadian users. Not a real clinical trial.",
    heroTitle: "Canadian Test Page",
    heroSubtitle: "For Canadian Users",
    introText: "This page demonstrates Canadian postal code support.",
    longDescription:
      "This is a test page that demonstrates Canadian postal code support in the HealthMatch platform. It uses the Canadian postal code format (A1A 1A1) for validation and adapts the UI accordingly.\n\nAny data submitted through this page is for testing purposes only and will not be processed as a real trial application.",
    preScreeningQuestions: lpTestScreeningQuestions,
    conditionId: 304,
    preferredLoginMethod: LoginMethod.SMS,
    trialIdToAutoApply: "CATEST",
    countryCode: "CA",
    status: "development",
  },

  // Direct redirect example - demonstrates the pattern for studies that handle
  // their own pre-screening on an external site. No questionnaire is shown;
  // the user sees a branded landing page with a CTA button that redirects
  // to the study's external URL with tracking params.
  "dev-direct-redirect": {
    id: "dev-direct-redirect",
    template: LandingPageTemplate.EXTENDED,
    title: "DEV ONLY - Direct Redirect Example | HealthMatch",
    description:
      "Development example of a direct redirect landing page for external pre-screening.",
    heroTitle: "Join a Clinical Study",
    heroSubtitle: "Complete your application on the study website",
    longDescription:
      "This study is investigating new treatments for a medical condition. " +
      "Click below to visit the study website where you can complete your application and pre-screening.\n\n" +
      "HealthMatch partners with research sites to help connect you with clinical trials that may be right for you.",
    conditionId: 0,
    countryCode: "US",
    status: "development",
    directRedirect: true,
    qualifiedRedirectMessage:
      "We're directing you to the study website to complete your application.",
    externalRedirectUrl:
      "https://example.com/apply?utm_source=healthmatch&utm_medium=affiliate&utm_campaign=hm_lp_$redirectToken&affiliate=$source&lp=$landingPageId",
  },

  // ---------------------------------------------------------------------------
  // STABLE TEST FIXTURE — DO NOT PAUSE, RENAME, OR REMOVE.
  //
  // A synthetic direct-redirect landing page used by regression tests (e.g.
  // src/hooks/__tests__/useLandingPageConfig.test.ts) so that route/config
  // resolution can be exercised without depending on any real study, which may
  // be paused or activated at any time. Tests should reference this fixture
  // instead of production page IDs like `copd-r`.
  // ---------------------------------------------------------------------------
  "test-redirect-r": {
    id: "test-redirect-r",
    template: LandingPageTemplate.EXTENDED,
    title: "TEST FIXTURE - Direct Redirect | HealthMatch",
    description:
      "Synthetic direct-redirect fixture for regression tests. Not a real study.",
    heroTitle: "Test Redirect Study",
    heroSubtitle: "Regression test fixture",
    longDescription:
      "This is a stable test fixture used by the automated test suite. " +
      "It is not a real clinical trial and must remain active so tests do not " +
      "depend on the paused/active state of real landing pages.",
    conditionId: 0,
    countryCode: "US",
    status: "development",
    directRedirect: true,
    qualifiedRedirectMessage:
      "We're directing you to the study website to complete your application.",
    externalRedirectUrl:
      "https://example.com/apply?utm_source=healthmatch&utm_medium=app&utm_campaign=hm_lp_$redirectToken&utm_content=TEST_questionnaire",
  },
};
